import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { StripeFunctionError, startDonateCheckout } from '@/lib/stripe-client';

export const Route = createFileRoute('/donate')({
  component: DonatePage,
});

const PRESETS = [10, 25, 50, 100] as const;
const MIN = 1;
const MAX = 10_000;

/**
 * One-off donation page (PLAN §5, Increment 4). Public — no login required.
 * Preset tiers + a custom amount feed one path: startDonateCheckout(cents) →
 * Stripe Checkout (mode: payment, dynamic price_data). Entirely separate from
 * the Phase E subscription flow.
 */
function DonatePage() {
  const [selected, setSelected] = useState<number | null>(25);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);

  const amount = custom.trim() ? Number(custom) : selected;
  const valid =
    typeof amount === 'number' && Number.isFinite(amount) && amount >= MIN && amount <= MAX;

  const donate = async () => {
    if (!valid) {
      toast.error(`Enter an amount between $${MIN} and $${MAX.toLocaleString()}.`);
      return;
    }
    setBusy(true);
    try {
      const url = await startDonateCheckout(Math.round(amount * 100));
      window.location.href = url;
    } catch (e) {
      toast.error(
        e instanceof StripeFunctionError
          ? 'Could not start checkout. Try again.'
          : 'Something went wrong.',
      );
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-video-bg text-video-text">
      <div className="mx-auto max-w-md px-6 py-16">
        <h1 className="font-serif text-3xl text-video-text">Support ArcheTelos</h1>
        <p className="mt-3 font-sans text-sm text-video-text-muted">
          A one-time gift helps keep the record open. This is a donation, not a subscription —
          nothing recurring.
        </p>

        <div className="mt-8 grid grid-cols-4 gap-2">
          {PRESETS.map((p) => {
            const active = selected === p && !custom.trim();
            return (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setSelected(p);
                  setCustom('');
                }}
                className={`rounded-md border px-3 py-2 font-sans text-sm transition ${
                  active
                    ? 'border-oxblood-browse bg-oxblood-browse/15 text-video-text'
                    : 'border-video-line text-video-text-muted hover:border-silver'
                }`}
              >
                ${p}
              </button>
            );
          })}
        </div>

        <div className="mt-3">
          <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-video-text-faint">
            Custom amount (USD)
          </label>
          <input
            inputMode="decimal"
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              setSelected(null);
            }}
            placeholder="$"
            className="w-full rounded-md border border-video-line bg-video-panel px-3 py-2 font-sans text-sm text-video-text placeholder:text-video-text-faint focus:border-silver focus:outline-none"
          />
        </div>

        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => void donate()}
          className="mt-6 w-full rounded-md bg-oxblood-browse px-4 py-3 font-sans text-sm font-medium text-video-text transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Redirecting…' : valid ? `Donate $${amount.toLocaleString()}` : 'Donate'}
        </button>

        <a
          href="/"
          className="mt-4 block text-center font-sans text-xs text-silver hover:underline"
        >
          Back to browse
        </a>
      </div>
    </div>
  );
}
